import type {
  GameSession,
  ImportResult,
  ModuleManifest,
} from "../contracts/types";
import { GAME_SAVE_SCHEMA_VERSION } from "../contracts/types";

const MAX_PAYLOAD_BYTES = 256_000;

export type SessionEnvelope = {
  kind: "DECKOS_GAME_SESSION_SAVE";
  authorityClass: "GAME_SESSION";
  notADelta: true;
  notAnImprint: true;
  moduleId: string;
  moduleVersion: string;
  saveSchemaVersion: string;
  exportedAt: string;
  session: GameSession;
};

export function exportSession(session: GameSession): SessionEnvelope {
  return {
    kind: "DECKOS_GAME_SESSION_SAVE",
    authorityClass: "GAME_SESSION",
    notADelta: true,
    notAnImprint: true,
    moduleId: session.moduleId,
    moduleVersion: session.moduleVersion,
    saveSchemaVersion: session.saveSchemaVersion,
    exportedAt: new Date().toISOString(),
    session: structuredClone(session),
  };
}

export function serializeSession(session: GameSession): string {
  return JSON.stringify(exportSession(session), null, 2);
}

export function importSession(
  raw: string,
  module: ModuleManifest,
  current: GameSession,
): ImportResult {
  if (raw.length > MAX_PAYLOAD_BYTES)
    return { ok: false, error: "Save payload exceeds maximum size" };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: "Malformed JSON" };
  }

  if (!parsed || typeof parsed !== "object")
    return { ok: false, error: "Save is not an object" };

  const envelope = parsed as Partial<SessionEnvelope> & {
    session?: Partial<GameSession>;
  };

  // Accept either envelope or bare session for resilience
  const session = (envelope.session ?? envelope) as Partial<GameSession>;

  if (envelope.kind && envelope.kind !== "DECKOS_GAME_SESSION_SAVE")
    return { ok: false, error: "Not a game session save" };

  if (envelope.authorityClass && envelope.authorityClass !== "GAME_SESSION")
    return { ok: false, error: "Invalid authority class" };

  const moduleId = envelope.moduleId ?? session.moduleId;
  const moduleVersion = envelope.moduleVersion ?? session.moduleVersion;
  const schema =
    envelope.saveSchemaVersion ?? session.saveSchemaVersion;

  if (moduleId !== module.id)
    return { ok: false, error: `Module ID mismatch: ${moduleId}` };
  if (moduleVersion !== module.version)
    return { ok: false, error: `Module version mismatch: ${moduleVersion}` };
  if (schema !== GAME_SAVE_SCHEMA_VERSION)
    return { ok: false, error: `Save schema mismatch: ${schema}` };

  const sceneIds = new Set(module.scenes.map((s) => s.id));
  if (!session.currentSceneId || !sceneIds.has(session.currentSceneId))
    return { ok: false, error: "Invalid current scene" };

  for (const id of session.discovered ?? []) {
    if (!sceneIds.has(id))
      return { ok: false, error: `Unknown discovered scene: ${id}` };
  }
  for (const id of session.visited ?? []) {
    if (!sceneIds.has(id))
      return { ok: false, error: `Unknown visited scene: ${id}` };
  }

  const attrKeys = Object.keys(module.startingState.attributes);
  for (const key of Object.keys(session.attributes ?? {})) {
    if (!attrKeys.includes(key))
      return { ok: false, error: `Illegal attribute: ${key}` };
  }
  const resKeys = Object.keys(module.startingState.resources);
  for (const key of Object.keys(session.resources ?? {})) {
    if (!resKeys.includes(key))
      return { ok: false, error: `Illegal resource: ${key}` };
  }

  if (session.authorityClass && session.authorityClass !== "GAME_SESSION")
    return { ok: false, error: "Session authority class invalid" };
  if (session.events && (!Array.isArray(session.events) || session.events.some((event) =>
    !event || typeof event !== "object" || event.schemaVersion !== "1.0.0" ||
    event.authorityClass !== "GAME_SESSION_EVENT" || event.sessionId !== session.sessionId ||
    typeof event.sequence !== "number" || !event.evidence || typeof event.eventId !== "string")))
    return { ok: false, error: "Malformed campaign event" };
  if (session.campaignReviews && (!Array.isArray(session.campaignReviews) || session.campaignReviews.some((review) =>
    !review || typeof review !== "object" || !["HOLD", "REJECT", "CORRECT", "CAMPAIGN_IMPRINT"].includes(review.state) || !Array.isArray(review.evidence))))
    return { ok: false, error: "Malformed campaign review" };

  // On failure path callers keep `current`; on success return validated clone.
  void current;
  const restored: GameSession = {
    authorityClass: "GAME_SESSION",
    moduleId: module.id,
    moduleVersion: module.version,
    saveSchemaVersion: GAME_SAVE_SCHEMA_VERSION,
    sessionId: String(session.sessionId ?? `restored-${Date.now()}`),
    seed: String(session.seed ?? "ARCHIVIST-47"),
    currentSceneId: session.currentSceneId,
    selectedSceneId: session.selectedSceneId ?? session.currentSceneId,
    attributes: { ...module.startingState.attributes, ...session.attributes },
    resources: { ...module.startingState.resources, ...session.resources },
    inventory: [...(session.inventory ?? [])],
    flags: [...(session.flags ?? [])],
    lore: [...(session.lore ?? [])],
    discovered: [...(session.discovered ?? [])],
    visited: [...(session.visited ?? [])],
    transcript: [...(session.transcript ?? [])],
    history: [...(session.history ?? [])],
    actionCount: Number(session.actionCount ?? 0),
    ending: (session.ending as string | null) ?? null,
    saveRevision: Number(session.saveRevision ?? 0),
    lastOperationId: session.lastOperationId,
    updatedAt: new Date().toISOString(),
    events: [...(session.events ?? [])],
    campaignReviews: [...(session.campaignReviews ?? [])],
  };

  return { ok: true, session: restored };
}

const MEMORY_KEY = "deckos.gamebook.session.v1";

/** Local autosave / manual save — isolated from project chip DELTA store. */
export function saveSessionLocal(session: GameSession): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(MEMORY_KEY, serializeSession(session));
}

export function loadSessionLocal(
  module: ModuleManifest,
  fallback: GameSession,
): GameSession {
  if (typeof localStorage === "undefined") return fallback;
  const raw = localStorage.getItem(MEMORY_KEY);
  if (!raw) return fallback;
  const result = importSession(raw, module, fallback);
  return result.ok ? result.session : fallback;
}

export function clearSessionLocal(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(MEMORY_KEY);
}

export function saveSession(session: GameSession): GameSession {
  const next = {
    ...session,
    saveRevision: session.saveRevision + 0,
    updatedAt: new Date().toISOString(),
  };
  saveSessionLocal(next);
  return next;
}

export function loadSession(
  module: ModuleManifest,
  fallback: GameSession,
): GameSession {
  return loadSessionLocal(module, fallback);
}
